import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { X, Eraser, PenTool } from "lucide-react";
import { toast } from "sonner";

const PRIMARY = "#4F1B7C";
const PINK = "#EC1F7C";

/**
 * SignaturePadModal — modale de signature électronique au dessin (canvas).
 * Génère un data URL PNG (transparent fond) à partir du tracé.
 *
 * Props :
 *   - title : titre affiché dans la modale
 *   - signerName / signerTitle : pré-remplis (modifiables)
 *   - onClose : ferme la modale sans signer
 *   - onSign({ signatureDataUrl, signerName, signerTitle }) : callback de soumission
 */
export default function SignaturePadModal({
  title = "Signer la convention",
  signerName: initialName = "",
  signerTitle: initialTitle = "",
  onClose,
  onSign,
}) {
  const padRef = useRef(null);
  const [name, setName] = useState(initialName);
  const [titleStr, setTitleStr] = useState(initialTitle);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const clear = () => padRef.current?.clear();

  const submit = async () => {
    if (!name.trim()) { toast.error("Indiquez le nom du signataire"); return; }
    if (!padRef.current || padRef.current.isEmpty()) { toast.error("Veuillez dessiner votre signature"); return; }
    if (!agreed) { toast.error("Cochez la case d'engagement légal"); return; }
    setBusy(true);
    try {
      // PNG en data URL (canvas tient typiquement < 50 Ko pour une signature)
      const dataUrl = padRef.current.toDataURL("image/png");
      await onSign({ signatureDataUrl: dataUrl, signerName: name.trim(), signerTitle: titleStr.trim() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur signature");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg sm:rounded-3xl shadow-2xl max-h-[100vh] sm:max-h-[92vh] overflow-y-auto" data-testid="sig-pad-modal">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-pink-600 text-[10px] font-black uppercase tracking-widest">Signature électronique</div>
            <h2 className="font-blackhawk italic uppercase text-xl" style={{ color: PRIMARY }}>{title}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Fermer"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-slate-500">Nom complet du signataire *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full p-2.5 rounded-lg border border-slate-200 text-sm" placeholder="Prénom Nom" data-testid="sig-name"/>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider text-slate-500">Fonction</label>
              <input value={titleStr} onChange={(e) => setTitleStr(e.target.value)} className="mt-1 w-full p-2.5 rounded-lg border border-slate-200 text-sm" placeholder="Représentant légal" data-testid="sig-title"/>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-black uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5">
                <PenTool size={12}/> Tracez votre signature ci-dessous
              </label>
              <button type="button" onClick={clear} className="text-xs text-slate-500 hover:text-pink-600 inline-flex items-center gap-1" data-testid="sig-clear">
                <Eraser size={12}/> Effacer
              </button>
            </div>
            <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden" data-testid="sig-canvas-wrap">
              <SignatureCanvas
                ref={padRef}
                penColor="#1a1626"
                canvasProps={{
                  width: 500,
                  height: 180,
                  className: "w-full h-[180px] bg-white block touch-none",
                  "data-testid": "sig-canvas",
                }}
              />
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 italic">Touche / souris pour signer — utilisez la gomme pour recommencer.</p>
          </div>

          <label className="flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" data-testid="sig-agree"/>
            <span className="text-xs text-slate-600 leading-relaxed">
              Je certifie sur l'honneur être habilité(e) à engager mon organisation et reconnais que la signature électronique
              ci-dessus a la même valeur juridique qu'une signature manuscrite (eIDAS, art. 1366 et 1367 du Code civil).
            </span>
          </label>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-5 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100" data-testid="sig-cancel">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-5 py-2 rounded-lg text-white text-sm font-black uppercase tracking-wider shadow-md disabled:opacity-50"
            style={{ background: `linear-gradient(135deg, ${PINK}, #ff3a8c)` }} data-testid="sig-submit">
            {busy ? "Envoi…" : "Signer la convention"}
          </button>
        </div>
      </div>
    </div>
  );
}
